import { ComponentProps, forwardRef, FC } from 'react';
import styled from 'styled-components';
import { Themes } from '../../types';
import { getColor, noop } from '@gif/shared-global';
import { CloseIcon } from '@gif/shared-ui';

const black = getColor('black');
const white = getColor('white');

const themeColors = {
  light: black,
  dark: getColor('neutral100'),
};

const backgrounds = {
  light: white,
  dark: black,
};

const borders = {
  light: '#D2D2D2',
  dark: white,
};

const defaultHeight = '2.4rem';

const Wrapper = styled.div<{ $theme: keyof typeof themeColors }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  label {
    margin-bottom: 0.5rem;
    color: ${({ $theme }) => themeColors[$theme as keyof typeof themeColors]};
    font-size: 0.875rem;
  }
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const StyledInput = styled.input<{
  $hasError?: boolean;
  $showIcon?: boolean;
  $hasValid?: boolean;
  $theme: Themes;
}>`
  width: 100%;
  min-height: ${defaultHeight};
  padding: 0
    ${({ $showIcon }) => ($showIcon ? '3rem' : 'var(--space-space200, 1rem)')} 0
    var(--space-space200, 1rem);
  color: ${({ $theme }) => themeColors[$theme]};
  background-color: ${({ $theme }) => backgrounds[$theme]};
  border-radius: 0.125rem;
  border: 1px solid ${({ $theme }) => borders[$theme]};
  box-sizing: border-box;
  font-size: 0.84rem;
  ${({ $hasValid }) =>
    $hasValid &&
    `
    border-color: #00FF00;
  `};
  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #DC3545;
  `};
  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 1rem;
  display: flex;
  height: ${defaultHeight};
  align-items: center;
`;

const CrossIcon = styled(CloseIcon)<{ $theme: Themes }>`
  width: 1rem;
  height: 1rem;
  color: ${({ $theme }) => themeColors[$theme]};
  cursor: pointer;
`;

const ErrorMessageContainer = styled.div`
  height: 100%;
  p {
    margin: 0.25rem 0 0 0.25rem;
    color: #dc3545;
    font-size: 0.875rem;
  }
`;

// Types, Enums && Props
export type InputType = 'text' | 'password' | 'number' | 'email' | 'tel';

export interface InputProps extends Omit<ComponentProps<'input'>, 'ref'> {
  name: string;
  label: string;
  value?: string;
  defaultValue?: string;
  showLabel?: boolean;
  type?: InputType;
  className?: string;
  hasError?: boolean;
  errorMessage?: string;
  SvgIcon?: FC<React.SVGProps<SVGSVGElement>>;
  showIcon?: boolean;
  onIconClick?: (e: React.MouseEvent) => void;
  hasValid?: boolean;
  themeId?: Themes;
}

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    className,
    errorMessage,
    hasError,
    label,
    name,
    value, // use the `value` prop only for controlled components
    defaultValue, // use the `defaultValue` prop only for uncontrolled components
    showLabel,
    type = 'text',
    SvgIcon = CrossIcon,
    showIcon,
    onIconClick = noop,
    hasValid,
    themeId = Themes.LIGHT,
    ...rest
  } = props;

  return (
    <Wrapper className={className} $theme={themeId}>
      {showLabel && <label htmlFor={name}>{label}</label>}
      <InputContainer>
        <StyledInput
          {...rest}
          ref={ref}
          $hasError={hasError}
          $hasValid={hasValid}
          $showIcon={showIcon}
          id={name}
          name={name}
          type={type}
          {...(typeof defaultValue !== 'undefined' &&
            typeof value === 'undefined' && { defaultValue })}
          {...(typeof value !== 'undefined' &&
            typeof defaultValue === 'undefined' && { value })}
          aria-invalid={!!errorMessage}
          aria-errormessage={`${name}-error`}
          aria-label={label}
          $theme={themeId}
        />
        {showIcon && (
          <IconContainer>
            <SvgIcon onClick={onIconClick} $theme={themeId} />
          </IconContainer>
        )}
      </InputContainer>
      <ErrorMessageContainer>
        {hasError && errorMessage && (
          <p id={`${name}-error`} aria-live="assertive">
            {errorMessage}
          </p>
        )}
      </ErrorMessageContainer>
    </Wrapper>
  );
});

export default Input;
